import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Cottage Stay", "Full Estate Takeover", "Wedding", "Retreat", "Something else"];

const inputCls =
  "w-full rounded-xl border border-[#5A4632]/20 bg-white px-4 py-3 text-sm text-[#241A12] placeholder-[#7A6A58]/60 outline-none transition-colors focus:border-[#B85D43] focus:ring-2 focus:ring-[#B85D43]/20";

export default function EnquiryForm({ defaultInterest = "Cottage Stay" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: defaultInterest,
    arrival_date: "",
    guests: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        guests: form.guests ? Number(form.guests) : null,
      });
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="enquiry-success" className="rounded-2xl border border-[#8A6D4B]/30 bg-[#8A6D4B]/10 p-10 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#8A6D4B] text-[#F4F1EA]">
          <Check className="h-7 w-7" />
        </span>
        <h3 className="mt-6 font-serif text-3xl text-[#241A12]">Diolch — thank you.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#7A6A58]">
          Your enquiry has landed at the Plas. We read every one personally and will reply within a day or two —
          the goats permitting.
        </p>
        <button
          data-testid="enquiry-another-button"
          onClick={() => {
            setSent(false);
            setForm({ name: "", email: "", phone: "", interest: defaultInterest, arrival_date: "", guests: "", message: "" });
          }}
          className="mt-8 rounded-full border border-[#5A4632]/30 px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-[#5A4632] transition-colors hover:border-[#B85D43] hover:text-[#B85D43]"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            Name *
          </label>
          <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            Email *
          </label>
          <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enquiry-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            Phone
          </label>
          <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="Optional" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-interest" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            I'm interested in
          </label>
          <select id="enquiry-interest" data-testid="enquiry-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
            {INTERESTS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enquiry-date" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            Arrival date
          </label>
          <input id="enquiry-date" data-testid="enquiry-date-input" type="date" value={form.arrival_date} onChange={set("arrival_date")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="enquiry-guests" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
            Guests
          </label>
          <input id="enquiry-guests" data-testid="enquiry-guests-input" type="number" min="1" max="40" value={form.guests} onChange={set("guests")} placeholder="Up to 40" className={inputCls} />
        </div>
      </div>
      <div>
        <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A6A58]">
          Tell us about your stay
        </label>
        <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Dates, occasions, dogs, dreams — anything that helps." className={inputCls} />
      </div>
      <button
        type="submit"
        data-testid="enquiry-submit-button"
        disabled={sending}
        className="w-full rounded-full bg-[#B85D43] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#5A4632] disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
}
